import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { setUnauthorizedHandler } from '../config/api';
import * as authService from '../service/authService';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const clearSession = useCallback(() => {
        setUser(null);
    }, []);

    useEffect(() => {
        setUnauthorizedHandler(clearSession);

        // The session cookie is httpOnly, so there's nothing to check in JS
        // first — just ask the API whether we're authenticated.
        authService
            .me()
            .then((data) => setUser(data.user))
            .catch(() => clearSession())
            .finally(() => setLoading(false));
    }, [clearSession]);

    const login = useCallback(async (email, password) => {
        const data = await authService.login(email, password);
        setUser(data.user);
        return data.user;
    }, []);

    const register = useCallback(async (name, email, password, passwordConfirmation) => {
        const data = await authService.register(name, email, password, passwordConfirmation);
        setUser(data.user);
        return data.user;
    }, []);

    const logout = useCallback(async () => {
        try {
            await authService.logout();
        } finally {
            clearSession();
        }
    }, [clearSession]);

    const value = useMemo(
        () => ({
            user,
            loading,
            isAuthenticated: !!user,
            isAdmin: user?.role === 'admin',
            login,
            register,
            logout,
        }),
        [user, loading, login, register, logout]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return ctx;
}
